// Карточка поста. Отдельного адреса «один пост» в контракте нет — строка берётся из списка
// постов объекта, который уже загружен колонкой слева.
//
// Если часов в сутки больше, чем могут отстоять охранники поста, это сказано первым, над
// полями: в списке слева на это места хватает только на метку «часы не сходятся», а что не так
// и что сделать, человек читает здесь (`post-hours.ts`).

import {
  RiBuilding4Line,
  RiErrorWarningLine,
  RiPencilLine,
  RiShieldUserLine,
} from '@remixicon/react';
import { Link } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import { useAuthStore } from '@/shared/auth/store';
import { formatHours, hoursWord } from '@/shared/hours/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { usePostsQuery } from '@/shared/org/queries';
import { лишниеЧасыПоста } from '../post-hours';
import type { SitesRoute } from '../route';
import { openEditPostHref, sitesHref } from '../route';

const GUARD_FORMS = ['охранник', 'охранника', 'охранников'] as const;

/** Правка поста — то же право, что и заведение (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

export function PostCard({
  postId,
  siteId,
  route,
}: {
  postId: string;
  siteId: string;
  route: SitesRoute;
}) {
  const query = usePostsQuery(siteId);
  const post = query.data?.items.find((item) => item.id === postId);
  const правитьМожно = can(
    useAuthStore((state) => state.permissions),
    EDIT_PERMISSION,
  );
  const лишние =
    post === undefined ? null : лишниеЧасыПоста(post.hours_per_day, post.simultaneous_guards);

  return (
    <DetailPane
      title={
        <span className='flex items-center gap-2'>
          <RiShieldUserLine className='size-5 shrink-0 text-text-soft-400' />
          <span className='truncate'>{post?.name ?? 'Пост'}</span>
        </span>
      }
      actions={
        <>
          {/* Правка поста появилась 15.09.2026. До неё пост, заведённый с опечаткой в часах,
              нельзя было ни поправить, ни снять — а часы уезжают в норматив объекта и дальше
              в ставку человека. Единственным выходом было завести второй пост рядом, и тогда
              норматив складывался уже из обоих. */}
          {правитьМожно && post !== undefined && (
            <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
              <Link to={openEditPostHref(route, siteId, post.id)}>
                <Button.Icon as={RiPencilLine} />
                Править
              </Link>
            </Button.Root>
          )}
          <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
            <Link to={sitesHref('objects', { ...route.search, filters: {}, tab: 'posts' }, siteId)}>
              <Button.Icon as={RiBuilding4Line} />
              Карточка объекта
            </Link>
          </Button.Root>
        </>
      }
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='карточка поста'
        isEmpty={() => post === undefined}
        onRetry={() => void query.refetch()}
        loading={
          <div className='p-5'>
            <Skeleton className='h-24 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiShieldUserLine}
            title='Этого поста нет у выбранного объекта'
            description='Ссылка ведёт на пост другого объекта либо на пост, который уже убрали. Выберите объект заново — его посты появятся в списке слева.'
          />
        }
      >
        {() =>
          post === undefined ? null : (
            <div className='flex flex-col gap-4 p-5'>
              {лишние !== null && (
                <Alert.Root variant='lighter' status='warning' size='small' role='status'>
                  <Alert.Icon as={RiErrorWarningLine} />
                  <span>{лишние}</span>
                </Alert.Root>
              )}
              <FieldList>
                <FieldRow
                  label='Часов в сутки'
                  value={`${formatHours(post.hours_per_day)} ${hoursWord(post.hours_per_day)}`}
                  note='это слагаемое расчётного норматива объекта за месяц'
                />
                <FieldRow
                  label='Требуемый разряд охранника'
                  value={
                    post.required_guard_category === null
                      ? null
                      : `${post.required_guard_category} разряд`
                  }
                  missing='разряд не задан: на пост подойдёт охранник с любым разрядом'
                />
                <FieldRow
                  label='Одновременно на посту'
                  value={countLabel(post.simultaneous_guards, GUARD_FORMS)}
                  note='из этого числа складывается требуемая укомплектованность объекта'
                />
                {/* Название режима приходит с сервера: словарь живёт в контракте, и по нему же
                    строится график смен. Показывать здесь код («shift_24_12») значило бы
                    показывать человеку наше служебное слово. */}
                <FieldRow
                  label='Режим смен'
                  value={post.schedule_type_title ?? post.schedule_type}
                  missing='режим не задан: график смен по этому посту не построится'
                />
                <FieldRow
                  label='Состояние'
                  value={post.is_active ? 'действует' : 'не действует'}
                  note={
                    post.is_active
                      ? undefined
                      : 'недействующий пост не участвует ни в нормативе, ни в укомплектованности'
                  }
                />
              </FieldList>
            </div>
          )
        }
      </ScreenState>
    </DetailPane>
  );
}
